import { Ball } from './Ball.js';
import { countOf, fmtClaim, type GbSeatView } from './types.js';

// ---------------------------------------------------------------------------
// A seat
// ---------------------------------------------------------------------------
// FRONT ROW ON TOP, BACK (hidden) ROW UNDERNEATH — always, for every
// player, so the four hands read as one board rather than four little
// widgets. Balls are centred and evenly spaced: not squeezed together,
// not stretched edge to edge.
//
// The honesty tally accumulates across the whole game and is the primary
// tool for reading opponents, so it lives on the seat header where it is
// always visible.

export function SeatPanel({
  seat,
  committed,
  voted,
  isVoteTarget,
  identity,
  onSelectBall,
  selectedBallId,
  actions,
  compact = false,
  size = 'md'
}: {
  seat: GbSeatView;
  committed?: boolean;
  voted?: boolean;
  isVoteTarget?: boolean;
  identity?: string;
  /**
   * Nomination: your own hidden balls become the selection surface, so
   * the hand you declare on is the hand already on the board. Rendering
   * a second copy of it inside the declare sheet just made two of
   * everything.
   */
  onSelectBall?: (ballId: string) => void;
  selectedBallId?: string | null;
  /**
   * Your own buttons, rendered INSIDE this panel beside the balls. The
   * player zone is roughly three opponent zones wide; giving each button
   * its own full-width row underneath wasted the height the balls needed.
   */
  actions?: React.ReactNode;
  compact?: boolean;
  /** 'lg' for your own hand — it is the one you act on. */
  size?: 'sm' | 'md' | 'lg';
}) {
  const verified = seat.honesty.verifiedTrue + seat.honesty.verifiedLied;
  const ballSize = size === 'lg' ? 'lg' : compact ? 'sm' : 'md';

  return (
    <section
      className={[
        'flex flex-col gap-2 rounded-lg border p-2 transition',
        seat.alive ? 'border-ec-line bg-ec-panel' : 'border-ec-line/50 bg-ec-panel/40 opacity-60',
        seat.isYou ? 'ring-1 ring-ec-brass/60' : '',
        isVoteTarget ? 'ring-2 ring-ec-oxblood-2' : ''
      ].join(' ')}
    >
      {/* Your buttons ride the NAME LINE. That line is a short label and
          a status word with dead space after it; giving the buttons a
          row of their own underneath the balls cost ~44px of a phone for
          nothing, which is the "they shouldn't need their own row" note. */}
      <header
        className={`flex flex-wrap items-center gap-x-2 gap-y-1 ${
          actions ? 'justify-start text-left' : 'justify-center text-center'
        }`}
      >
        <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ec-face">
          {seat.label}
        </h3>
        {seat.isYou && (
          <span className="rounded-full border border-ec-brass/60 px-1.5 font-display text-[0.55rem] uppercase tracking-wide text-ec-brass">
            you
          </span>
        )}
        {seat.isBot === true && (
          <span className="rounded-full border border-ec-line px-1.5 font-display text-[0.55rem] uppercase tracking-wide text-ec-bone-dim">
            bot
          </span>
        )}
        {identity && (
          <span className="truncate font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
            {identity}
          </span>
        )}
        {!seat.alive && (
          <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-oxblood-2">
            out · round {seat.eliminatedRound}
          </span>
        )}
        {committed !== undefined && seat.alive && (
          <span
            className={`font-display text-[0.6rem] uppercase tracking-wide ${
              committed ? 'text-ec-go' : 'text-ec-bone-dim'
            }`}
          >
            {committed ? 'declared' : 'deciding…'}
          </span>
        )}
        {voted !== undefined && seat.alive && (
          <span
            className={`font-display text-[0.6rem] uppercase tracking-wide ${
              voted ? 'text-ec-go' : 'text-ec-bone-dim'
            }`}
          >
            {voted ? 'voted' : 'thinking…'}
          </span>
        )}
        {verified > 0 && (
          <span
            title={`${seat.honesty.verifiedTrue} true, ${countOf(seat.honesty.verifiedLied, 'lie')} (verified)`}
            className="font-display text-[0.6rem] uppercase tracking-wide"
          >
            <span className="text-ec-go">{seat.honesty.verifiedTrue}T</span>
            <span className="text-ec-bone-dim"> / </span>
            <span className="text-ec-oxblood-2">{seat.honesty.verifiedLied}L</span>
          </span>
        )}
        {actions && <span className="ml-auto flex shrink-0 gap-1.5">{actions}</span>}
      </header>

      {/* Rows on the left, YOUR buttons on the right. The player zone is
          about three opponent zones wide, so the actions live INSIDE it
          using width that was otherwise empty — never on their own
          full-width rows stacked underneath. */}
      {/* Balls and buttons side by side only when there is genuinely
          room (sm+). Below that they stack: `flex-wrap` with a shrink-0
          button column squeezed the ball rows into a narrow left strip,
          so a two-ball row wrapped into a VERTICAL tower of 5rem balls
          and ate the whole screen. */}
      <div className="flex min-w-0 flex-1 flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-4">
        {/* YOUR panel puts the two rows SIDE BY SIDE. Stacked they were
            216px of a 411px column and starved the claim picker down to
            53px — five of twenty-one values. Opponent panels stay
            stacked; they are narrow columns. */}
        <div
          className={`flex min-w-0 flex-1 justify-center gap-2 ${
            size === 'lg' ? 'flex-row items-start gap-4' : 'flex-col'
          }`}
        >
      {/* FRONT row on top. Centred, evenly spaced. */}
      <Row label="front" empty={seat.frontRow.length === 0}>
        {seat.frontRow.map((ball) => (
          <Ball
            key={ball.ballId}
            claim={ball.kind === 'killer' ? 'KILLER' : ball.value}
            size={ballSize}
          />
        ))}
      </Row>

      {/* BACK (hidden) row underneath. */}
      <Row label="hidden" empty={seat.backRow.length === 0}>
        {seat.backRow.map((ball) => {
          if (ball.revealedValue !== null) {
            return (
              <Ball key={ball.ballId} claim={ball.revealedValue} size={ballSize} label="revealed" />
            );
          }
          if (seat.isYou && ball.yourTruth !== null) {
            const lying = ball.claim !== null && ball.claim !== ball.yourTruth;
            return (
              <Ball
                key={ball.ballId}
                claim={ball.yourTruth}
                size={ballSize}
                state="yours"
                selected={onSelectBall ? ball.ballId === selectedBallId : undefined}
                onClick={onSelectBall ? () => onSelectBall(ball.ballId) : undefined}
                label={
                  ball.claim === null
                    ? 'undeclared'
                    : lying
                      ? `you say ${fmtClaim(ball.claim)}`
                      : 'told the truth'
                }
                title={
                  ball.claim === null
                    ? 'Only you can see this. Not declared yet.'
                    : `Only you can see this. You claimed ${fmtClaim(ball.claim)}.`
                }
              />
            );
          }
          return (
            <Ball
              key={ball.ballId}
              claim={ball.claim}
              size={ballSize}
              state="faceDown"
              label={ball.claim !== null ? 'claims' : ball.committed ? 'declared' : undefined}
              title={ball.claim !== null ? `Claimed ${fmtClaim(ball.claim)}` : 'Hidden'}
            />
          );
        })}
      </Row>
        </div>

      </div>

      {/* No vote button here, deliberately. These panels live in a
          scroller that can be shorter than they are, and a button
          clipped by an ancestor is not a button: at 360x640 they
          rendered below the clip line and hit-testing their centre hit
          a different element, so the vote was simply unreachable.
          Voting is a pinned row in the arena; this panel only carries
          the ring on whoever you picked. */}
    </section>
  );
}

/**
 * One row of balls: centred, with even gaps. `justify-center` plus a
 * fixed gap keeps a two-ball row and a five-ball row looking like the
 * same board — neither jammed together nor stretched to the edges.
 */
function Row({
  label,
  empty,
  children
}: {
  label: string;
  empty: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="font-display text-[0.5rem] uppercase tracking-widest text-ec-bone-dim">
        {label}
      </span>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {empty ? <span className="font-display text-[0.6rem] text-ec-bone-dim">—</span> : children}
      </div>
    </div>
  );
}
